import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { CARE_SNAPSHOT_STEPS } from "../../constants/careSnapshotSections";
import { getStorage } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../services/firebase";
import { useRole } from "../../contexts/RoleContext";
import { fetchProviders } from "../../services/providerService";
import { PERMISSIONS, canEditChildProfile, roleHasPermission } from "../../constants/roles";
import ChildProfileFlowContent from "./shared/ChildProfileFlowContent";
import ChildMedicationManager from "./shared/ChildMedicationManager";
import MedicationEditorSheet from "./shared/MedicationEditorSheet";
import { useAsyncForm } from "../../hooks/useAsyncForm";
import { canManageMedicationDefinitions } from "./shared/medicationPermissions";
import {
  archiveMedicationRecord,
  saveMedicationRecord,
  saveMedicationTimelineEvent,
} from "./shared/medicationPersistence";
import ChildProfileFlowPageShell from "./shared/ChildProfileFlowPageShell";
import { fetchMedications } from "../../services/medicationService";
import { buildEditChildProfileLoadState } from "./childProfileForm/editChildProfileLoad";
import { buildChildMedicalProfileUpdate } from "./childProfileForm/medicalProfileUpdate";
import {
  archiveProfileMedicationDetail,
  saveProfileMedicationDraft,
} from "./childProfileForm/profileMedicationPersistence";
import { migrateLegacyMedicationRecords } from "./childProfileForm/legacyMedicationMigration";
import ProfileSectionSaveFooter from "./childProfileForm/ProfileSectionSaveFooter";
import {
  getProfileSectionLabel,
  normalizeChildCondition,
} from "./childProfileForm/profileFormHelpers";
import {
  buildChildProfileSavePayload,
  saveChildProfilePhoto,
} from "./childProfileForm/profilePhotoSave";
import {
  retryProfileSaveFollowUps,
  saveChildProfileWithFollowUps,
} from "./childProfileForm/profileSaveOrchestrator";
import { getChildCareTeamMemberCount } from "../../utils/profileCompletion";
import { useProfileSnapshotTracker } from "./childProfileForm/profileSnapshot";
import { useChildProfileFields } from "./childProfileForm/useChildProfileFields";
import { useProfileDocuments } from "./childProfileForm/useProfileDocuments";
import { resolveProfileMedicationDetails } from "./childProfileForm/profileMedicationHydration";
import { useProfileMedicationEditor } from "./childProfileForm/useProfileMedicationEditor";
import { getChildAccentTheme } from "./shared/childAccentTheme";
import { PROFILE_ADD_INTENTS } from "./shared/UniversalProfileAddSheet";
import { CARE_PROFILE_SECTION_IDS } from "../../utils/careProfileSections";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";
import {
  buildMedicationReviewReturnState,
  getMedicationReviewNavigationContext,
} from "../../utils/medicationReviewNavigation";
import { syncProfileGoalTrackersForChild } from "../../features/tracking/profileGoalTrackerService";

const EditChildModal = ({
  onClose,
  child,
  onSuccess,
  userRole,
  initialStep = 0,
  initialProfileChapterId = null,
  initialProfileSectionId = "",
  initialProfileSectionLabel = "",
  openInboundEmailOnLoad = false,
  onDirectProfileChapterBack = null,
  bottomNavigation = null,
  openMedicationEditorOnLoad = false,
  medicationReviewNavigation = null,
  onOpenMedicationReview,
  onOpenMedicationReminders,
  onViewTodayMedications: _onViewTodayMedications,
}) => {
  const { t } = useTranslation(['terms', 'common']);
  const navigate = useNavigate();
  const storage = getStorage();
  const { childrenWithAccess = [], refreshRoles } = useRole();
  const latestChildRef = React.useRef(child);
  latestChildRef.current = child;
  const [stage, setStage] = useState("intake");
  const [currentStep, setCurrentStep] = useState(0);
  const [activeProfileSectionId, setActiveProfileSectionId] = useState(initialProfileSectionId);
  const [activeProfileSectionLabel, setActiveProfileSectionLabel] = useState(initialProfileSectionLabel);
  // Owned here (not inside CareProfileOverview) so it survives the round trip through
  // a Care Setup destination's own step: Back/Save must restore Care Setup, not the
  // root Profile landing. See navigation audit notes on returnToProfileDashboard.
  const [profileLandingView, setProfileLandingView] = useState(initialProfileChapterId ? "handbook" : null);
  // One level deeper than profileLandingView: which Handbook chapter (if any) is
  // open. null = Handbook table of contents. Same "owned in the parent" reasoning.
  const [handbookChapterId, setHandbookChapterId] = useState(initialProfileChapterId);
  // True when the open chapter was reached by skipping the table of
  // contents (e.g. Quick Access jumps straight to a chapter). Back must
  // then return straight to the Profile root, not the unseen TOC.
  const [handbookChapterQuickEntry, setHandbookChapterQuickEntry] = useState(Boolean(initialProfileChapterId));
  const [submitError, setSubmitError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [sectionSaveStatus, setSectionSaveStatus] = useState("idle");
  const [sectionSaveWarnings, setSectionSaveWarnings] = useState([]);
  const [retryingSectionWarning, setRetryingSectionWarning] = useState(false);
  const latestSectionSaveWarningsRef = React.useRef([]);
  const sectionSaveLockRef = React.useRef(false);
  const [medicationSaveStatus, setMedicationSaveStatus] = useState("idle");
  const [medicationSaveError, setMedicationSaveError] = useState("");
  const [medicationSaveWarning, setMedicationSaveWarning] = useState("");
  const [providers, setProviders] = useState([]);
  const resolvedMedicationReviewNavigation = React.useMemo(
    () => getMedicationReviewNavigationContext(medicationReviewNavigation || {}),
    [medicationReviewNavigation]
  );
  const hasMedicationReviewNavigation = resolvedMedicationReviewNavigation.hasOriginContext;
  const medicationSaveLockRef = React.useRef(false);
  const {
    name,
    setName,
    age,
    setAge,
    dateOfBirth,
    setDateOfBirth,
    pronouns,
    setPronouns,
    language,
    setLanguage,
    photo,
    setPhoto,
    photoURL,
    setPhotoURL,
    selectedConditions,
    setSelectedConditions,
    foodAllergies,
    setFoodAllergies,
    dietaryRestrictions,
    setDietaryRestrictions,
    sensoryIssues,
    setSensoryIssues,
    behavioralTriggers,
    setBehavioralTriggers,
    communicationNeeds,
    setCommunicationNeeds,
    careContext,
    setCareContext,
    supportProfile,
    setSupportProfile,
    setProfileFields,
    resetProfileFields,
  } = useChildProfileFields();
  const canEdit = canEditChildProfile(userRole);
  // Handbook source-routing pilot (DM_02): reuses the existing VIEW_MEDICAL_INFO
  // permission as the authoritative "can this user see clinical/therapy
  // documents" gate - there is no more specific "document viewing" permission
  // in the role matrix today, and VIEW_MEDICAL_INFO already draws exactly this
  // line (care_owner/care_partner/therapist yes, caregiver no).
  const canViewDocuments = userRole === "parent" || roleHasPermission(userRole, PERMISSIONS.VIEW_MEDICAL_INFO);
  const currentUserId = auth?.currentUser?.uid || "";
  const canEditMedications = canManageMedicationDefinitions({ currentUserId, child, userRole });
  const {
    medicationDetails,
    setMedicationDetails,
    medicationDraft,
    editingMedicationId,
    isMedicationEditorOpen,
    updateMedicationDraft,
    clearMedicationDraft,
    addMedicationDetail,
    editMedicationDetail,
    resetMedicationEditor,
    loadMedicationDetails,
  } = useProfileMedicationEditor({ canEditMedication: canEditMedications });
  const {
    documentSectionsRef,
    uploadedDocuments,
    openSections,
    setOpenSections,
    resetDocuments,
    renderDocumentDropZone,
  } = useProfileDocuments({
    storage,
    childId: child?.id || "",
    setDocumentError,
  });
  const { hasUnsavedChanges, markCurrentSnapshot, resetSnapshot } = useProfileSnapshotTracker({
    name,
    age,
    dateOfBirth,
    pronouns,
    language,
    photoURL,
    photoName: photo?.name || "",
    selectedConditions,
    foodAllergies,
    dietaryRestrictions,
    sensoryIssues,
    behavioralTriggers,
    communicationNeeds,
    careContext,
    supportProfile,
    medicationDetails,
    uploadedDocuments,
  });

  // Use async form hook for child editing
  const childForm = useAsyncForm({
    onSuccess: () => {},
    validate: ({ name, dateOfBirth, requireDateOfBirth = false }) => {
      if (!name?.trim()) {
        throw new Error(`Please enter the ${t('terms:profile_name').toLowerCase()}`);
      }
      if (requireDateOfBirth && !dateOfBirth?.trim()) {
        throw new Error("Please enter the date of birth.");
      }
    }
  });

  const resetChildForm = childForm.reset;

  useEffect(() => {
    let isActive = true;
    const loadedProfile = buildEditChildProfileLoadState(latestChildRef.current);

    if (loadedProfile.childId) {
      const initialChapterId = initialProfileChapterId || null;
      setProfileFields(loadedProfile);
      setStage("intake");
      setCurrentStep(Number.isFinite(initialStep) ? initialStep : 0);
      setActiveProfileSectionId(initialProfileSectionId);
      setProfileLandingView(initialChapterId ? "handbook" : null);
      setHandbookChapterId(initialChapterId);
      setHandbookChapterQuickEntry(Boolean(initialChapterId));
      setActiveProfileSectionLabel(initialProfileSectionLabel);

      const updateMedicationState = (nextMedicationDetails) => {
        loadMedicationDetails(nextMedicationDetails, {
          openEditorOnLoad: openMedicationEditorOnLoad,
          medicationReviewSuggestion: resolvedMedicationReviewNavigation.medicationSuggestion,
          medicationReviewSource: {
            id: resolvedMedicationReviewNavigation.originSourceId,
            childId: loadedProfile.childId,
            rawText: resolvedMedicationReviewNavigation.sourceText,
            sourceContext: 'timeline',
            sourceMetadata: { fileName: 'Timeline note' },
          },
        });
        markCurrentSnapshot({
          ...loadedProfile.snapshotState,
          medicationDetails: nextMedicationDetails,
        });
      };

      updateMedicationState(loadedProfile.profileMedicationDetails);
      resetDocuments(loadedProfile.profileDocuments);
      Promise.resolve()
        .then(() => fetchMedications(loadedProfile.childId, true))
        .then(async (medicationsFromCollection) => {
          if (!isActive) {
            return;
          }

          const collectionMedicationDetails = resolveProfileMedicationDetails({
            medicationRecords: medicationsFromCollection,
          });
          if (collectionMedicationDetails.length) {
            updateMedicationState(collectionMedicationDetails);
            return;
          }

          const migratedMedicationDetails = await migrateLegacyMedicationRecords({
            childId: loadedProfile.childId,
            medicalProfile: loadedProfile.medicalProfile,
          });
          if (migratedMedicationDetails.length) {
            updateMedicationState(migratedMedicationDetails);
          }
        })
        .catch((error) => {
          console.error("Error loading saved medications for child profile:", error);
        });
      
      // Reset form hook state when child data loads
      resetChildForm();
    }

    return () => {
      isActive = false;
    };
  }, [
    canEditMedications,
    child?.id,
    initialProfileChapterId,
    initialProfileSectionId,
    initialProfileSectionLabel,
    initialStep,
    loadMedicationDetails,
    markCurrentSnapshot,
    openMedicationEditorOnLoad,
    resolvedMedicationReviewNavigation,
    resetChildForm,
    resetDocuments,
    setProfileFields,
  ]);

  useEffect(() => {
    let isMounted = true;

    if (!child?.id) {
      setProviders([]);
      return undefined;
    }

    const loadProviders = async () => {
      try {
        const nextProviders = await fetchProviders(child.id);
        if (isMounted) {
          setProviders(nextProviders);
        }
      } catch (error) {
        if (isMounted) {
          setProviders([]);
        }
      }
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, [child?.id]);

  const resetForm = () => {
    setStage("intake");
    setCurrentStep(0);
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
    setProfileLandingView(null);
    setHandbookChapterId(null);
    setSubmitError("");
    setDocumentError("");
    setSectionSaveStatus("idle");
    sectionSaveLockRef.current = false;
    resetProfileFields();
    resetMedicationEditor();
    resetDocuments();
    resetSnapshot();
    childForm.reset();
  };

  const handleClose = () => {
    if (canEdit && hasUnsavedChanges && !window.confirm("You have unsaved changes. Leave this page?")) {
      return;
    }

    if (onClose) {
      onClose();
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const buildMedicalProfileUpdate = (detailList = medicationDetails) => (
    buildChildMedicalProfileUpdate({
      name,
      age,
      dateOfBirth,
      photoURL,
      selectedConditions,
      foodAllergies,
      dietaryRestrictions,
      sensoryIssues,
      behavioralTriggers,
      communicationNeeds,
      careContext,
      supportProfile,
      medicationRecords: detailList,
      profileDocuments: documentSectionsRef.current,
    })
  );

  const saveProfileBasics = async ({ refreshRolesAfterSave = false } = {}) => {
    const childRef = doc(db, "children", child.id);
    let profilePatch;
    const result = await saveChildProfileWithFollowUps({
      uploadPhoto: () => saveChildProfilePhoto({
        storage,
        childId: child.id,
        photo,
        fallbackPhotoURL: child.photoURL || "",
      }),
      buildProfilePatch: (savedPhotoURL) => {
        profilePatch = buildChildProfileSavePayload({
          name,
          age,
          dateOfBirth,
          photoURL: savedPhotoURL,
          selectedConditions,
          medicalProfileUpdate: buildMedicalProfileUpdate(),
        });
        return profilePatch;
      },
      updateChild: (profilePatch) => updateDoc(childRef, profilePatch),
      secondaryTasks: [
        {
          operation: "goal_tracker_sync",
          label: "Goal tracker synchronization",
          service: "Firestore",
          path: `trackers?childId=${child.id}`,
          run: () => syncProfileGoalTrackersForChild({
            childId: child.id,
            childName: name,
            goals: profilePatch.supportProfile?.careGoals || [],
          }),
        },
        ...(refreshRolesAfterSave ? [{
          operation: "role_refresh",
          label: "Care-circle role refresh",
          service: "Firestore",
          path: `children/${child.id}`,
          run: () => Promise.resolve(refreshRoles?.()),
        }] : []),
      ],
    });

    if (!result.primarySaved) {
      throw result.failure.error;
    }

    if (photo) setPhoto(null);
    setPhotoURL(result.photoURL);
    markCurrentSnapshot({
      photoURL: result.photoURL,
      photoName: "",
    });
    latestSectionSaveWarningsRef.current = result.warnings;
    setSectionSaveWarnings(result.warnings);
    result.warnings.forEach((warning) => {
      console.error("Profile follow-up failed", {
        operation: warning.operation,
        service: warning.service,
        path: warning.path,
        childId: child.id,
        uid: auth.currentUser?.uid || "",
        role: userRole || "",
        code: warning.code,
      });
    });

    return result.profilePatch;
  };

  const handleAdvanceBasicInfo = ({ returnToSetup = true } = {}) => {
    if (!canEdit) {
      throw new Error('You do not have permission to edit this child\'s information');
    }

    if (!child) {
      throw new Error('Child data is not available');
    }

    setSubmitError("");
    return childForm.submitForm(
      async () => {
        const updatedChild = await saveProfileBasics({ refreshRolesAfterSave: true });
        if (returnToSetup) {
          setStage("setup");
          setCurrentStep(0);
        }
        return updatedChild;
      },
      { name, dateOfBirth, requireDateOfBirth: true }
    ).catch((error) => {
      setSubmitError(error?.message || "Could not save this profile right now.");
    });
  };

  const handleSubmit = ({ closeAfterSave = true } = {}) => {
    // Security check - ensure user has permission to edit
    if (!canEdit) {
      throw new Error('You do not have permission to edit this child\'s information');
    }

    if (!child) {
      throw new Error('Child data is not available');
    }

    return childForm.submitForm(
      async () => {
        const updatedChild = await saveProfileBasics({ refreshRolesAfterSave: true });

        if (onSuccess && closeAfterSave) {
          onSuccess(updatedChild);
        } else if (closeAfterSave) {
          resetForm();
          if (onClose) {
            onClose();
          } else {
            navigate("/dashboard", { replace: true });
          }
        } else {
          setStage("setup");
        }

        return updatedChild;
      },
      { name, dateOfBirth }
    );
  };

  const getMedicationCreatedBy = () => (
    child?.users?.care_owner || child?.users?.members?.[0] || ""
  );

  const resetMedicationSaveFeedback = () => {
    setMedicationSaveStatus("idle");
    setMedicationSaveError("");
    setMedicationSaveWarning("");
  };

  const handleCloseMedicationEditor = () => {
    if (medicationSaveLockRef.current) return;
    clearMedicationDraft();
    resetMedicationSaveFeedback();
  };

  const handleAddMedication = (draft) => {
    resetMedicationSaveFeedback();
    addMedicationDetail(draft);
  };

  const handleEditMedication = (entry) => {
    resetMedicationSaveFeedback();
    editMedicationDetail(entry);
  };

  const syncMedicationProfileUpdate = (detailList) => {
    const childRef = doc(db, "children", child.id);
    const profileUpdate = buildMedicalProfileUpdate(detailList);

    return updateDoc(childRef, {
      ...profileUpdate,
      medicalProfile: {
        ...profileUpdate.medicalProfile,
        sleepIssues: child?.medicalProfile?.sleepIssues || [],
      },
    });
  };

  const handleSaveMedicationDraft = async () => {
    if (medicationSaveLockRef.current) return null;

    medicationSaveLockRef.current = true;
    setMedicationSaveStatus("saving");
    setMedicationSaveError("");
    setMedicationSaveWarning("");

    try {
      const result = await saveProfileMedicationDraft({
        medicationDetails,
        medicationDraft,
        editingMedicationId,
        childId: child?.id || "",
        createdBy: getMedicationCreatedBy(),
        canEditMedication: canEditMedications,
        setSubmitError: setMedicationSaveError,
        setMedicationDetails,
        clearMedicationDraft,
        clearDraftOnSuccess: false,
        persistMedicationRecord: saveMedicationRecord,
        persistMedicationTimelineEvent: saveMedicationTimelineEvent,
        onNonCriticalError: ({ stage }) => {
          if (stage === "timeline") {
            setMedicationSaveWarning("Medication saved, but its Timeline update could not be recorded.");
          }
        },
        syncMedicationDetails: syncMedicationProfileUpdate,
        markCurrentSnapshot,
      });

      if (!result || result.status === "partial") {
        setMedicationSaveStatus("failed");
        if (result?.error) setMedicationSaveError(result.error);
        return result;
      }

      setMedicationSaveStatus("saved");
      if (hasMedicationReviewNavigation) {
        await handleMedicationReviewComplete();
        return result;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
      clearMedicationDraft();
      setMedicationSaveStatus("idle");
      setMedicationSaveError("");
      setMedicationSaveWarning("");
      return result;
    } catch (error) {
      const message = error?.message || "Could not save this medication right now.";
      setMedicationSaveStatus("failed");
      setMedicationSaveError(message);
      return null;
    } finally {
      medicationSaveLockRef.current = false;
    }
  };

  const handleArchiveMedication = (entry, archived = true) => archiveProfileMedicationDetail({
    medicationDetails,
    entry,
    childId: child?.id || "",
    createdBy: getMedicationCreatedBy(),
    archived,
    archivedBy: getMedicationCreatedBy(),
    canEditMedication: canEditMedications,
    requiresChildId: true,
    setSubmitError,
    setMedicationDetails,
    clearMedicationDraft,
    editingMedicationId,
    persistMedicationArchiveRecord: archiveMedicationRecord,
    syncMedicationDetails: syncMedicationProfileUpdate,
    markCurrentSnapshot,
  });

  const handleMedicationListChanged = ({ type, medication } = {}) => {
    if (!medication?.id) return;

    setMedicationDetails((current) => {
      if (type === "removed") {
        return current.filter((entry) => entry.id !== medication.id);
      }
      const exists = current.some((entry) => entry.id === medication.id);
      if (exists) {
        return current.map((entry) => (
          entry.id === medication.id ? medication : entry
        ));
      }
      return [...current, medication];
    });
  };

  const renderMedicationDetails = () => (
      <ChildMedicationManager
        childId={child?.id || ""}
        childName={titleName}
        medications={medicationDetails}
        providers={providers}
        onEditMedication={handleEditMedication}
        onAddMedication={handleAddMedication}
        onArchiveMedication={handleArchiveMedication}
        onMedicationListChanged={handleMedicationListChanged}
        onMedicationReviewComplete={handleMedicationReviewComplete}
        medicationReviewNavigation={resolvedMedicationReviewNavigation}
        onOpenMedicationReview={onOpenMedicationReview}
        onOpenMedicationReminders={onOpenMedicationReminders}
        canEditMedication={canEditMedications}
        userRole={userRole}
      />
    );

  const saveCurrentSection = async () => {
    if (sectionSaveLockRef.current) {
      return;
    }

    sectionSaveLockRef.current = true;
    setSectionSaveStatus("saving");
    setSubmitError("");
    latestSectionSaveWarningsRef.current = [];
    setSectionSaveWarnings([]);

    try {
      const updatedChild = currentStep === CARE_SNAPSHOT_STEPS.ABOUT
        ? await handleAdvanceBasicInfo({ returnToSetup: false })
        : await handleSubmit({ closeAfterSave: false });
      if (!updatedChild) {
        setSectionSaveStatus("failed");
        return;
      }

      setSectionSaveStatus("saved");
      if (latestSectionSaveWarningsRef.current.length) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 650));
      if (onDirectProfileChapterBack) {
        onDirectProfileChapterBack();
        return;
      }
      setCurrentStep(0);
      setActiveProfileSectionId("");
      setActiveProfileSectionLabel("");
    } catch (error) {
      console.error("Failed to save section:", error);
      setSubmitError(error?.message || "Could not save this section right now.");
      setSectionSaveStatus("failed");
    } finally {
      sectionSaveLockRef.current = false;
    }
  };

  const retrySectionWarnings = async () => {
    if (!sectionSaveWarnings.length || retryingSectionWarning) return;
    setRetryingSectionWarning(true);
    const remainingWarnings = await retryProfileSaveFollowUps(sectionSaveWarnings);
    latestSectionSaveWarningsRef.current = remainingWarnings;
    setSectionSaveWarnings(remainingWarnings);
    setRetryingSectionWarning(false);
  };

  const handleProfileStepChange = (nextStep) => {
    setSectionSaveStatus("idle");
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
    setCurrentStep(nextStep);
  };

  const handleProfileSectionSelect = (section) => {
    if (!section?.targetStep) return;
    setSectionSaveStatus("idle");
    setActiveProfileSectionId(section.id || "");
    setActiveProfileSectionLabel(section.label || "");
    setCurrentStep(section.targetStep);
  };

  const returnToProfileDashboard = () => {
    if (sectionSaveLockRef.current) return;
    setSectionSaveStatus("idle");
    setCurrentStep(0);
    setActiveProfileSectionId("");
    setActiveProfileSectionLabel("");
  };

  const handleMedicationReviewComplete = React.useCallback(async () => {
    if (!hasMedicationReviewNavigation || !resolvedMedicationReviewNavigation.returnTo) {
      return;
    }

    if (resolvedMedicationReviewNavigation.originEntryId) {
      await updateDoc(doc(db, 'dailyLogs', resolvedMedicationReviewNavigation.originEntryId), {
        'metadata.medicationReview.status': 'resolved',
        'metadata.medicationReview.resolvedAt': new Date().toISOString(),
        'metadata.medicationReview.resolvedBy': auth.currentUser?.uid || null,
        'metadata.needsReview': false,
      });
    }

    navigate(resolvedMedicationReviewNavigation.returnTo, {
      replace: true,
      state: buildMedicationReviewReturnState(resolvedMedicationReviewNavigation),
    });
  }, [hasMedicationReviewNavigation, navigate, resolvedMedicationReviewNavigation]);

  const handleProfileAddIntent = (intent) => {
    if (intent === PROFILE_ADD_INTENTS.TIMELINE) {
      navigate("/dashboard", {
        state: {
          openQuickEntryForChildId: child?.id || "",
          returnTo: CARE_PROFILE_ROUTE,
          returnToState: { childId: child?.id || "" },
        },
      });
      return;
    }

    if (intent === PROFILE_ADD_INTENTS.MEDICATION) {
      handleProfileSectionSelect({
        id: CARE_PROFILE_SECTION_IDS.MEDICATION,
        label: "Medication",
        targetStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
      });
      return;
    }

    if (intent === PROFILE_ADD_INTENTS.GOAL) {
      handleProfileSectionSelect({
        id: CARE_PROFILE_SECTION_IDS.GOALS,
        label: "Goals",
        targetStep: CARE_SNAPSHOT_STEPS.GOALS_GROWTH,
      });
    }
  };

  const sectionSaveFooter = (
    <ProfileSectionSaveFooter
      currentStep={currentStep}
      sectionLabel={activeProfileSectionLabel}
      disabled={childForm.loading || !hasUnsavedChanges || sectionSaveStatus === "saved"}
      hasUnsavedChanges={hasUnsavedChanges}
      showStatus
      saveStatus={sectionSaveStatus}
      warning={sectionSaveWarnings.length
        ? `Profile saved. ${sectionSaveWarnings.map((warning) => warning.label).join(", ")} could not finish.`
        : ""}
      retryingWarning={retryingSectionWarning}
      onSave={saveCurrentSection}
      onRetryWarning={retrySectionWarnings}
    />
  );
  const titleName = child?.name || name || "Child";
  const childAccent = React.useMemo(
    () => getChildAccentTheme(child || { id: child?.id || "", name, photoURL }, { children: childrenWithAccess }),
    [child, childrenWithAccess, name, photoURL]
  );
  const isMedicationStep = currentStep === CARE_SNAPSHOT_STEPS.HEALTH_CARE;
  const careTeamMemberCount = getChildCareTeamMemberCount(child || {});
  const activeSectionLabel = activeProfileSectionLabel || getProfileSectionLabel(currentStep);
  const shellTitle = !canEdit
    ? `${titleName}'s Care Story`
    : currentStep === 0
      ? "Care Profile"
      : activeSectionLabel;
  const shellSubtitle = !canEdit
    ? "Need to know and care story"
    : currentStep === 0
      ? ""
      : isMedicationStep
        ? `${titleName} · Health & Care`
        : titleName;

  return (
    <ChildProfileFlowPageShell
      title={shellTitle}
      subtitle={shellSubtitle}
      onBack={
        !canEdit
          ? handleClose
          : currentStep !== 0
            ? (onDirectProfileChapterBack || returnToProfileDashboard)
            // At the Profile root (currentStep 0) but drilled into Handbook/Care
            // Setup: the app-bar Back must match the in-page Back button so the
            // two controls never perform different actions (pop one drill level,
            // don't exit the whole profile). Handbook has an extra level
            // (chapter -> table of contents -> root), so pop it first.
            : profileLandingView === "handbook" && handbookChapterId
              ? () => {
                if (handbookChapterQuickEntry && onDirectProfileChapterBack) {
                  onDirectProfileChapterBack();
                  return;
                }
                setHandbookChapterId(null);
                if (handbookChapterQuickEntry) {
                  setProfileLandingView(null);
                  setHandbookChapterQuickEntry(false);
                }
              }
              : profileLandingView
                ? () => setProfileLandingView(null)
                : handleClose
      }
      footer={canEdit && currentStep > 0 && currentStep < CARE_SNAPSHOT_STEPS.REVIEW ? sectionSaveFooter : null}
      bottomNavigation={bottomNavigation}
      navigationDisabled={sectionSaveStatus === "saving"}
    >
      <ChildProfileFlowContent
        mode="edit"
        stage={stage}
        canEditProfile={canEdit}
        canViewDocuments={canViewDocuments}
        openInboundEmailOnLoad={openInboundEmailOnLoad}
        currentStep={currentStep}
        onStepChange={handleProfileStepChange}
        submitError={submitError}
        setSubmitError={setSubmitError}
        documentError={documentError}
        setDocumentError={setDocumentError}
        name={name}
        setName={setName}
        age={age}
        setAge={setAge}
        dateOfBirth={dateOfBirth}
        setDateOfBirth={setDateOfBirth}
        pronouns={pronouns}
        setPronouns={setPronouns}
        language={language}
        setLanguage={setLanguage}
        setPhoto={setPhoto}
        photoURL={photoURL}
        setPhotoURL={setPhotoURL}
        selectedConditions={selectedConditions}
        setSelectedConditions={setSelectedConditions}
        normalizeCondition={normalizeChildCondition}
        foodAllergies={foodAllergies}
        setFoodAllergies={setFoodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        setDietaryRestrictions={setDietaryRestrictions}
        sensoryIssues={sensoryIssues}
        setSensoryIssues={setSensoryIssues}
        behavioralTriggers={behavioralTriggers}
        setBehavioralTriggers={setBehavioralTriggers}
        communicationNeeds={communicationNeeds}
        setCommunicationNeeds={setCommunicationNeeds}
        careContext={careContext}
        setCareContext={setCareContext}
        supportProfile={supportProfile}
        setSupportProfile={setSupportProfile}
        child={child}
        childAccent={childAccent}
        childId={child?.id || ""}
        renderDocumentDropZone={renderDocumentDropZone}
        renderMedicationDetails={renderMedicationDetails}
        medicationDetails={medicationDetails}
        providers={providers}
        careTeamMemberCount={careTeamMemberCount}
        activeProfileSectionId={activeProfileSectionId}
        activeProfileSectionLabel={activeSectionLabel}
        landingView={profileLandingView}
        onLandingViewChange={setProfileLandingView}
        handbookChapterId={handbookChapterId}
        onHandbookChapterIdChange={setHandbookChapterId}
        handbookChapterQuickEntry={handbookChapterQuickEntry}
        onHandbookChapterQuickEntryChange={setHandbookChapterQuickEntry}
        childDisplayName={titleName}
        onProfileSectionSelect={handleProfileSectionSelect}
        onOpenCareTeam={() => navigate("/care-team")}
        onOpenProviders={() => navigate("/providers", {
          state: {
            childId: child?.id || null,
            returnTo: CARE_PROFILE_ROUTE,
          },
        })}
        onOpenCollectedInformation={() => navigate("/care-knowledge", {
          state: { childId: child?.id || null },
        })}
        onOpenPlans={child?.id ? () => navigate(`/children/${child.id}/plans`, {
          state: { childId: child.id },
        }) : undefined}
        onProfileAddIntent={handleProfileAddIntent}
        uploadedDocuments={uploadedDocuments}
        openSections={openSections}
        setOpenSections={setOpenSections}
        createdChildName={child?.name || name}
        isSubmitting={childForm.loading}
        onCreate={canEdit ? handleAdvanceBasicInfo : undefined}
        onFinish={canEdit ? () => handleSubmit({ closeAfterSave: true }) : undefined}
        onSaveDraft={canEdit ? () => handleSubmit({ closeAfterSave: false }) : undefined}
        onClose={handleClose}
        t={t}
      />

      {canEdit ? (
        <MedicationEditorSheet
          open={isMedicationEditorOpen}
          onClose={handleCloseMedicationEditor}
          childName={titleName}
          child={child || { name: titleName, careContext, conditions: selectedConditions }}
          draft={medicationDraft}
          isEditing={Boolean(editingMedicationId)}
          onDraftChange={updateMedicationDraft}
          onSaveDraft={handleSaveMedicationDraft}
          onClearDraft={handleCloseMedicationEditor}
          providers={providers}
          saveStatus={medicationSaveStatus}
          saveError={medicationSaveError}
          saveWarning={medicationSaveWarning}
        />
      ) : null}
    </ChildProfileFlowPageShell>
  );
};

export default EditChildModal;
